import { create } from 'zustand';
import { useQuery } from '@tanstack/react-query';
import { listWorkspaces, type Workspace } from '../features/workspaces/api';

/**
 * The workspace the whole app is looking at.
 *
 * ONE selection, in the header, rather than a control per page. It used to live
 * on the upload page and mean only "where the next upload goes"; it now also
 * decides what the study list shows and which uploads appear in the history, so
 * every view answers the same question the same way and there is one place to
 * change the answer.
 *
 * Held here rather than in the URL because it is a property of the person, not
 * of the page: it should survive navigating from the list into a slide and back,
 * and a link someone pastes to a colleague should open THEIR workspace, not the
 * sender's.
 */
const KEY = 'workspace.selected';
/** What the upload page's own picker used before this became app-wide. Read once
 *  so nobody's existing choice is silently forgotten on upgrade. */
const LEGACY_KEY = 'upload.workspace';

function initial(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(KEY) ?? window.localStorage.getItem(LEGACY_KEY);
}

interface WorkspaceState {
  /** The public id the person chose, which may no longer be one they are in. */
  selected: string | null;
  setSelected: (publicId: string | null) => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  selected: initial(),
  setSelected: (publicId) => {
    if (typeof window !== 'undefined') {
      if (publicId) window.localStorage.setItem(KEY, publicId);
      else window.localStorage.removeItem(KEY);
      window.localStorage.removeItem(LEGACY_KEY);
    }
    set({ selected: publicId });
  },
}));

export interface SelectedWorkspace {
  /** The workspaces this account can read, viewers included, default first. */
  workspaces: Workspace[];
  /** The one in effect, or undefined until the list has loaded. */
  current: Workspace | undefined;
  /**
   * False until we know which workspace is in effect.
   *
   * Callers must not query the catalogue before this is true. Asking without the
   * filter and re-asking with it shows the full list for one frame and then
   * takes rows away, which reads as a bug in the filter rather than as loading.
   */
  ready: boolean;
}

/**
 * Resolves the stored choice against what this account is actually in.
 *
 * The stored id is a WISH, not an entitlement: memberships change, workspaces
 * get archived, and the same browser may sign in as somebody else. So it is
 * matched against the live list every time and falls back to their default —
 * never sent to the server unresolved, which would only earn a 403.
 */
export function useSelectedWorkspace(enabled = true): SelectedWorkspace {
  const selected = useWorkspaceStore((s) => s.selected);
  // `enabled` false: a guest in the community shell, who has no workspaces
  // and whose request would only earn a 401.
  const { data, isLoading } = useQuery({
    queryKey: ['workspaces'],
    queryFn: listWorkspaces,
    staleTime: 5 * 60_000,
    enabled,
  });
  // Archived workspaces grant nothing — the server drops them from scope — so
  // offering one would be a filter that always returns empty.
  const workspaces = (data?.workspaces ?? []).filter((w) => !w.archived);
  const current =
    workspaces.find((w) => w.public_id === selected) ??
    workspaces.find((w) => w.is_default) ??
    workspaces[0];
  return { workspaces, current, ready: enabled && !isLoading };
}
